import type { BlockTrialSlot } from '@/components/admin/sessions/walk/walk-block-parts';
import { WalkBlockEdge, WalkEdge } from '@/components/admin/sessions/walk/walk-edge';
import { feedbackEdgeNote, protocolVisitIndex } from '@/components/admin/sessions/walk/walk-labels';
import { WalkNoyauBlock } from '@/components/admin/sessions/walk/walk-noyau-block';
import type { WalkNoyauExpectedBandProps } from '@/components/admin/sessions/walk/walk-noyau-expected-band';
import { WalkPageCard } from '@/components/admin/sessions/walk/walk-page-card';
import { WalkProtocolBlock } from '@/components/admin/sessions/walk/walk-protocol-block';
import { WalkRangementBlock } from '@/components/admin/sessions/walk/walk-rangement-block';
import { noyauSitesOfPage } from '@/lib/session-noyau';
import { rangementBlockDrawn } from '@/lib/session-rangement';
import type { SessionDetail } from '@matter_extractor/shared';
import { Fragment } from 'react';

/**
 * The fil of §12.2: the WHOLE session, page by page, in the order it traversed
 * them.
 *
 * ## A page entered twice appears twice
 *
 * The walk is the list of VISITS and not the list of pages: a reprise (§5.3)
 * walks a page again, so the session recorded a second traversal and the screen
 * shows a second card. Deduplicating would turn two passes into one, which is
 * the fact somebody opening a parcours is most likely looking for.
 *
 * ## The protocol block stands at the visit that holds the turns
 *
 * `protocolVisitIndex` says which one, by the clock (see `walk-labels.ts`).
 * When no visit holds a turn - a session that stopped before the protocol, one
 * whose page was empty, one with no turn at all - the block is APPENDED, so that
 * « une page protocole vide, ou jamais atteinte, le dit plutôt que de
 * disparaître » holds even when there is no visit to hang it on. No edge is
 * drawn above it, and that is the point: an edge means « the session went from
 * here to there », and this is the block saying it never did. A plain gap says
 * « this is not part of the fil » where a connector would have said the
 * opposite.
 *
 * ## The determination of the noyau follows the protocol block (#152)
 *
 * « Après le bloc du protocole, un bloc Détermination du noyau » (noyau.md
 * §9.1): it is not a page the session entered, so it is not a card of the walk
 * - it hangs on the protocol block that launched it, indented under a navy rule
 * (`WalkBlockEdge`, #224: how it came is said by its foot), and only on a session
 * that HAS a determination. The pages of the
 * sequence that follow are ordinary cards carrying their Noyau block.
 *
 * ## The rangement comes first, when there is one (#177)
 *
 * « Sous le bloc du protocole, un bloc Rangement de la matière » (scenario-succes.md
 * §7.1): the ranger runs before the determination and the determination reads
 * its column, so its block stands between the two. It is drawn when the session
 * HAS a rangement, or when the protocol it played carries objective targets - a
 * missing rangement then reads « aucun » - and not at all for a protocol with no
 * target, whose only door to the ranger is the blank rangement.
 *
 * ## A page played in the conversation is a card with its bubble (issue #186)
 *
 * Its visit is a visit like any other, so the card is drawn in place, and it says
 * « jouée dans la conversation » and leads to its bubble on the Conversation
 * screen (`walk-page-card.tsx`). The bubble is looked up by the visit's rank.
 *
 * ## The page of retour is a card like the others (issue #176)
 *
 * « Une carte du fil comme les autres » (retour-experience.md §8): its values
 * come grouped by the server like any page's, and a Note among them is worded
 * « 4 / 5 » from the NATURE the inventory carries for its block. The one thing
 * that differs is the edge above it on a CLOSED session, which says how the
 * person came (`feedbackEdgeNote`); nothing marks the card itself.
 */

export interface SessionWalkProps {
  readonly detail: SessionDetail;
  /**
   * The visit to ring a moment (0-based, in `detail.pages`): the card a line of
   * the Variables tab led back to (#172), null otherwise.
   */
  readonly flashVisit?: number | null;
  /** Ring the block « Détermination du noyau » a moment: the pill of the head led here (#218). */
  readonly flashNoyau?: boolean;
  /**
   * The dry trial of the analyst, drawn in the foot of the block « Détermination du noyau » and
   * under it (#224): the route's, because it reads and writes through the query client. Absent
   * (the walk mounted alone), the foot says its sentence of origin and nothing else.
   */
  readonly noyauTrial?: BlockTrialSlot | undefined;
  /** The same for the ranger, in the foot of the block « Rangement de la matière ». */
  readonly rangementTrial?: BlockTrialSlot | undefined;
}

/**
 * What the band « Face à l'attendu » reads (npc.md §26.3, #216, lot K): the expected noyau the
 * server served on the replay, the NPC it played and the threshold of its first choice - undefined
 * without an expected noyau, which is a real session, a replay with no NPC or an NPC with none.
 */
function expectedBandOf(detail: SessionDetail): WalkNoyauExpectedBandProps | undefined {
  const reading = detail.replay?.expected ?? null;
  const determination = detail.noyau?.determination ?? null;
  if (reading === null || determination === null) return undefined;
  const first = detail.noyau?.choices.find((choice) => choice.mode === 'first-choice') ?? null;
  return {
    expected: reading,
    npc: detail.replay?.npc ?? null,
    scores: determination.scores,
    threshold: first?.maxGap ?? null,
  };
}

/** `exactOptionalPropertyTypes`: an absent note is an absent prop, never `undefined`. */
function noteOf(note: string | null): { readonly note?: string } {
  return note === null ? {} : { note };
}

export function SessionWalk({
  detail,
  flashVisit = null,
  flashNoyau = false,
  noyauTrial,
  rangementTrial,
}: SessionWalkProps) {
  const protocolAt = protocolVisitIndex(detail);
  // The rank of the page the two blocks hang on, which their header says (« Bloc de la page 5 »).
  const protocolRank = protocolAt === null ? null : protocolAt + 1;
  const determination = detail.noyau?.determination ?? null;
  // The nature of a variable, by the key the session stores it under - the
  // block id of a field, which is what a value of the walk carries.
  const kinds = new Map(detail.variables.map((entry) => [entry.key, entry.kind] as const));
  // The bubble of each visit (#186): a page played in the conversation is matched
  // to its bubble by the visit - the rank both screens print - and by its page.
  const bubbles = new Map(
    (detail.conversationBubbles ?? []).map((bubble) => [bubble.visitIndex, bubble] as const),
  );
  const expectedBand = expectedBandOf(detail);
  const determinationBlock =
    determination === null ? null : (
      <>
        <WalkBlockEdge />
        <WalkNoyauBlock
          sessionId={detail.session.id}
          determination={determination}
          rank={protocolRank}
          foot={noyauTrial}
          // The reading the analyst was served is the FROZEN protocol's (#154).
          protocol={detail.cascade.protocol}
          flash={flashNoyau}
          expectedBand={expectedBand}
        />
      </>
    );

  const rangementBlock = rangementBlockDrawn(detail) ? (
    <>
      <WalkBlockEdge />
      <WalkRangementBlock detail={detail} rank={protocolRank} foot={rangementTrial} />
    </>
  ) : null;

  return (
    <div className="flex flex-col">
      {detail.pages.map((page, position) => {
        const sites = noyauSitesOfPage(detail, page.pageId);
        const found = bubbles.get(position);
        const bubble = found?.pageId === page.pageId ? found : undefined;
        return (
          // The key carries the POSITION as well as the page: two visits of one
          // page are two cards, and a key on the identifier alone would make React
          // reuse the first card for the second traversal.
          <Fragment key={`${page.pageId}-${position}`}>
            {position === 0 ? null : (
              <WalkEdge
                transition={null}
                {...noteOf(feedbackEdgeNote(detail.session.status, protocolAt, position))}
              />
            )}
            {position === protocolAt ? (
              <>
                <WalkProtocolBlock
                  detail={detail}
                  page={page}
                  rank={position + 1}
                  flash={position === flashVisit}
                />
                {rangementBlock}
                {determinationBlock}
              </>
            ) : (
              <WalkPageCard
                page={page}
                rank={position + 1}
                status={detail.session.status}
                kinds={kinds}
                flash={position === flashVisit}
                bubble={bubble === undefined ? null : { sessionId: detail.session.id, bubble }}
                noyau={
                  sites.length === 0 || detail.noyau === null
                    ? null
                    : { sites, reading: detail.noyau }
                }
              />
            )}
          </Fragment>
        );
      })}

      {protocolAt === null ? (
        <div className={detail.pages.length === 0 ? '' : 'mt-5'}>
          <WalkProtocolBlock detail={detail} page={null} rank={null} />
          {rangementBlock}
          {determinationBlock}
        </div>
      ) : null}
    </div>
  );
}
